/**
 * The engines heard: the player's own, and the nearest of the traffic's.
 *
 * Each is a synthesiser on the audio thread (`workers/engine.worklet.ts`)
 * fed from the powertrain as it is now (`core/audio/engine.ts`). The
 * traffic's are heard as they pass: placed in 3D, shifted by Doppler as they
 * close and go, dulled by the air with distance, and sharing a handful of
 * voices among the nearest cars, as an ear picks out the nearest few and
 * hears the rest as the town's hum (`world.ts`).
 */

import type { EngineKind } from '../../core/audio/engine.js';
import { airCutoff, dopplerFactor, engineNote, MOTO_GEARS, motoGear } from '../../core/audio/engine.js';
import type { At, EngineVoice, SoundSystem } from './system.js';

/** The traffic's voices: the nearest this many, heard out to `HEARD` metres. */
const TRAFFIC_VOICES = 6;
const HEARD = 160;
/** The automatic's gears, for a traffic car's revs. */
const CAR_TOPS = [13, 22, 31, 40, 48, 56];
const HEAVY_TOPS = [4, 7, 11, 15, 19, 22, 25];

/** A vehicle in the traffic, as the sound needs it. */
export interface TrafficSound {
  id: number;
  kind: EngineKind;
  x: number;
  y: number;
  z: number;
  /** Velocity, scene m/s. */
  vx: number;
  vz: number;
  speed: number;
}

/** What engine a traffic vehicle has: lorries and buses diesels, vans mostly; cars a mix, a share electric. */
export function trafficEngine(species: string, id: number): EngineKind {
  if (species === 'truckBox' || species === 'truckSemi') return 'truck';
  if (species.startsWith('bus')) return 'bus';
  if (species === 'parkedMoto') return 'moto';
  if (species === 'parkedScooter') return 'escooter';
  const roll = ((id * 2654435761) >>> 0) / 4294967296;
  if (species === 'carVan' || species === 'carPickup') return roll < 0.8 ? 'diesel' : 'petrol';
  return roll < 0.2 ? 'electric' : roll < 0.5 ? 'diesel' : 'petrol';
}

interface Heard {
  voice: EngineVoice;
  speed: number;
  /** Its revs' gear, and how hard it is pulling, smoothed. */
  gear: number;
  load: number;
  braking: number;
}

export class EngineVoices {
  private player: EngineVoice | undefined;
  private playerKey = '';
  private motoGear = 1;
  private readonly heard = new Map<number, Heard>();
  private readonly others = new Map<string, EngineVoice>();
  /** The player's vehicle whose engine was last heard running: back in it, it is still warm, no starter. */
  private startedKey = '';
  /** Heavy vehicles that have just pulled up: their air brakes let go. */
  onAirBrake: ((at: At) => void) | undefined;

  constructor(private readonly sound: SoundSystem) {}

  /**
   * The player's engine: `kind` undefined on foot (any engine stops).
   * `key` names the vehicle, so getting into another starts its own.
   */
  updatePlayer(
    kind: EngineKind | undefined, key: string, at: At,
    s: { throttle: number; speed: number; gear: number; tops: readonly number[]; spool: number; shifting: number; slip: number; offRoad: boolean; airborne: boolean; running: boolean; misfire: number },
  ): void {
    if (kind === undefined || !this.sound.live) {
      if (this.player !== undefined) {
        // Out of the car: it ticks over a moment and stops, heard from outside;
        // a rotor takes its time to spin down.
        this.player.set({ running: 0 });
        const voice = this.player;
        setTimeout(() => voice.stop(), voice.kind === 'heli' ? 9500 : 1500);
        this.player = undefined;
        this.playerKey = '';
      }
      return;
    }
    if (this.player === undefined || this.playerKey !== key) {
      this.player?.stop();
      this.player = this.sound.engine(kind, hash(key), key === this.startedKey, { running: 1 }, at, 5);
      this.startedKey = key;
      this.playerKey = this.player === undefined ? '' : key;
      this.motoGear = 1;
    }
    const voice = this.player;
    if (voice === undefined) return;
    const moto = kind === 'moto';
    if (moto) this.motoGear = motoGear(s.speed, this.motoGear);
    const note = engineNote(kind, {
      gear: moto ? this.motoGear : s.gear, tops: moto ? MOTO_GEARS : s.tops, speed: s.speed,
      // In the air the wheels spin free: the revs flare with the throttle.
      throttle: s.throttle, spool: moto ? Math.max(0, s.throttle) : s.spool, shifting: s.shifting, running: s.running,
    });
    const airborne = s.airborne && kind !== 'heli';
    voice.set({
      firing: note.firing * (airborne && s.throttle > 0 ? 1.25 : 1), load: note.load, revs: note.revs,
      spool: kind === 'heli' ? 0 : s.spool, speed: airborne ? 0 : Math.abs(s.speed), slip: s.slip,
      offRoad: s.offRoad ? 1 : 0, misfire: s.misfire, running: s.running ? 1 : 0,
      road: kind === 'heli' ? 0 : 1, doppler: 1, air: 18000, gain: kind === 'heli' ? 0.9 : 1,
    });
    voice.place(at);
  }

  /** The traffic heard from `listener` going (`lvx`, `lvz`), from `cars` about. */
  updateTraffic(cars: readonly TrafficSound[], listener: At, lvx: number, lvz: number, dt: number): void {
    const live = this.sound.live;
    const near = live
      ? cars
        .map(c => ({ c, d: Math.hypot(c.x - listener.x, c.y - listener.y, c.z - listener.z) }))
        .filter(n => n.d < HEARD)
        .sort((a, b) => a.d - b.d)
        .slice(0, TRAFFIC_VOICES)
      : [];
    const keep = new Set(near.map(n => n.c.id));
    for (const [id, h] of this.heard) {
      if (keep.has(id)) continue;
      h.voice.stop();
      this.heard.delete(id);
    }
    for (const { c, d } of near) {
      let h = this.heard.get(c.id);
      if (h === undefined) {
        const voice = this.sound.engine(c.kind, c.id, true, {}, c, 6);
        if (voice === undefined) continue;
        h = { voice, speed: c.speed, gear: 1, load: 0.3, braking: 0 };
        this.heard.set(c.id, h);
      }
      // How hard it pulls, from how it speeds up: cruising is a light load.
      const accel = dt > 0 ? (c.speed - h.speed) / dt : 0;
      const heavy = c.kind === 'truck' || c.kind === 'bus';
      const pulling = accel > 0.15 ? Math.min(1, 0.45 + accel / (heavy ? 1 : 2.5)) : accel < -0.4 ? 0 : 0.25;
      h.load += (pulling - h.load) * Math.min(1, dt * 4);
      h.braking = accel < -0.8 ? 1 : Math.max(0, h.braking - dt);
      // A heavy vehicle pulling up: its air brakes sigh.
      if (heavy && h.speed > 0.6 && c.speed <= 0.6 && h.braking > 0) this.onAirBrake?.(c);
      h.speed = c.speed;
      const tops = heavy ? HEAVY_TOPS : CAR_TOPS;
      while (h.gear < tops.length && c.speed > tops[h.gear - 1] * 0.9) h.gear++;
      while (h.gear > 1 && c.speed < tops[h.gear - 2] * 0.6) h.gear--;
      const note = engineNote(c.kind, {
        gear: h.gear, tops, speed: c.speed, throttle: h.load > 0.05 ? 1 : 0, spool: h.load, shifting: 0, running: true,
      });
      // Closing on the listener: along the line between them, the source's velocity less the listener's.
      const rx = listener.x - c.x, rz = listener.z - c.z, r = Math.hypot(rx, rz) || 1;
      const closing = ((c.vx - lvx) * rx + (c.vz - lvz) * rz) / r;
      h.voice.set({
        firing: note.firing, load: note.load, revs: note.revs, spool: h.load, speed: c.speed, slip: 0,
        running: 1, doppler: dopplerFactor(closing), air: airCutoff(d), road: 1, gain: 0.8,
      });
      h.voice.place(c);
    }
  }

  /**
   * Another engine heard, by `key`: the police helicopter overhead, say.
   * `at` undefined when it is gone.
   */
  updateOther(key: string, kind: EngineKind, at: At | undefined, listener: At, closing: number): void {
    let voice = this.others.get(key);
    if (at === undefined || !this.sound.live) {
      voice?.stop();
      this.others.delete(key);
      return;
    }
    const d = Math.hypot(at.x - listener.x, at.y - listener.y, at.z - listener.z);
    if (d > 1500) {
      voice?.stop();
      this.others.delete(key);
      return;
    }
    if (voice === undefined) {
      voice = this.sound.engine(kind, hash(key), true, {}, at, 20);
      if (voice === undefined) return;
      this.others.set(key, voice);
    }
    const note = engineNote(kind, { gear: 1, tops: [], speed: 0, throttle: 0.2, spool: 0, shifting: 0, running: true });
    voice.set({ ...note, running: 1, road: 0, doppler: dopplerFactor(closing), air: airCutoff(d), gain: 1 });
    voice.place(at);
  }

  /** Engines sounding now: the player's, the traffic's and the rest. */
  get count(): number {
    return (this.player === undefined ? 0 : 1) + this.heard.size + this.others.size;
  }

  stopAll(): void {
    for (const v of this.others.values()) v.stop();
    this.others.clear();
    this.player?.stop();
    this.player = undefined;
    this.playerKey = '';
    for (const h of this.heard.values()) h.voice.stop();
    this.heard.clear();
  }
}

function hash(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return h >>> 0;
}
